/* eslint-disable max-lines-per-function */
import { MouseEventHandler, ReactNode } from 'react';

import { Button, XmarkIcon, CaretDownIcon } from '../../index';
import { addStyles } from '../../utils/helpers';

export interface QuickFilterButtonProps {
  /** The main text or content to display on the button. */
  label?: ReactNode;
  /** An icon displayed before the button content. \nShared property passed from parent component. */
  icon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
  /** A number representing the count to be displayed next to the button content.
   * Shared property passed from parent component. */
  selectedCount?: number;
  /** Determines the visual state of the button.  \nShared property passed from parent component. */
  isSelected?: boolean;
  /** Event handler for when the button is clicked. */
  onClick?: MouseEventHandler<HTMLElement>;
  /** Event handler for when the X button is clicked. */
  onClose?: MouseEventHandler<HTMLButtonElement>;
}

const options = {
  container: {
    base: 'flex flex-nowrap items-center box-border rounded-lg border-solid border h-min w-fit cursor-pointer pl-5',
    unselected:
      'border-gray-600 bg-white text-gray-900 hover:text-primary hover:fill-primary active:border-jungle-900 active:fill-jungle-900 active:text-jungle-900',
    selected: 'border-jungle-600 bg-jungle-50 text-jungle-900 fill-jungle-900',
  },
  text: {
    base: 'text-base select-none pr-1',
    selected: '',
    unselected: '',
  },
  icon: {
    base: 'h-6 w-6',
    selected: 'fill-inherit',
    unselected: '',
  },
};

const displayCount = (count: number | undefined): string => {
  return count !== undefined && count > 0 ? ` (${count})` : '';
};

const QuickFilterButton = (props: QuickFilterButtonProps) => {
  const { label, selectedCount, icon, isSelected, onClick, onClose } = props;

  return (
    <div
      className={addStyles([
        options.container.base,
        isSelected ? options.container.selected : options.container.unselected,
      ])}
    >
      {icon && (
        <div className="pr-2">
          {icon({
            className: addStyles([options.icon.base, isSelected ? options.icon.selected : options.icon.unselected]),
          })}
        </div>
      )}
      <button className="select-none pr-1 text-base" onClick={onClick}>
        {label}
        {displayCount(selectedCount)}
      </button>
      {isSelected ? (
        <Button icon={XmarkIcon} onClick={onClose} type="action" />
      ) : (
        <Button icon={CaretDownIcon} onClick={onClick} type="inherit" />
      )}
    </div>
  );
};

export { QuickFilterButton };
